import { useGetWorkflowDetails } from '@automata/api/apiComponents';
import { WorkflowDetailed } from '@automata/api/apiSchemas';
import { TextField, TrackedButton } from '@automata/ui';
import { getErrorMessage } from '@automata/utils';
import { useRenameWorkflow } from '@automata/wfb-api/src/apis/wfbApiComponents';
import { RenameWorkflowInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import Box from '@mui/system/Box';
import Stack from '@mui/system/Stack';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { AsyncStatus } from 'components/AsyncStatus';
import { Placeholder } from 'components/Placeholder';
import { useSnackbar } from 'hooks/useSnackbar';

interface FormWrapperProps {
  workflow: WorkflowDetailed;
  refetchData: () => Promise<void>;
  onClose: () => void;
  requestAuthorisation?: string;
}

const FormWrapper = ({
  workflow,
  refetchData,
  onClose,
  requestAuthorisation,
}: FormWrapperProps): JSX.Element => {
  const {
    mutateAsync: renameWorkflow,
    isLoading: isRenaming,
    error,
  } = useRenameWorkflow({
    onSuccess: refetchData,
  });

  const { enqueueSnackbar } = useSnackbar();

  const defaultValues: RenameWorkflowInput = {
    name: `${workflow.name}`,
  };

  const { handleSubmit, register, formState } = useForm<RenameWorkflowInput>({
    defaultValues,
  });

  const onSubmit = useCallback(
    async (formData: RenameWorkflowInput) => {
      try {
        await renameWorkflow({
          body: {
            name: formData.name,
          },
          pathParams: { workflowId: workflow.id },
          ...(requestAuthorisation && {
            headers: { 'X-Request-Authorization': requestAuthorisation },
          }),
        });
        enqueueSnackbar('Workflow renamed', { variant: 'success' });
        onClose();
      } catch (err: unknown) {
        const error = getErrorMessage(err, 'unknown error');
        enqueueSnackbar(`Cannot rename workflow: ${error}`, {
          variant: 'error',
        });
      }
    },
    [
      enqueueSnackbar,
      onClose,
      renameWorkflow,
      workflow.id,
      requestAuthorisation,
    ]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <Box>
          <Stack spacing={2}>
            <TextField
              margin="dense"
              label="Workflow Name"
              fullWidth
              {...register('name')}
              data-cy="wflow-form-name"
            />
          </Stack>
        </Box>
        <Box>{error !== null && <AsyncStatus error={error as any} />}</Box>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <TrackedButton
            trackLabel="form-cancel-rename"
            disabled={isRenaming}
            color="secondary"
            variant="text"
            onClick={onClose}
          >
            Cancel
          </TrackedButton>
          <TrackedButton
            trackLabel="form-submit-rename"
            loading={isRenaming}
            disabled={!formState.isValid}
            variant="contained"
            type="submit"
          >
            Rename
          </TrackedButton>
        </Stack>
      </Stack>
    </form>
  );
};

interface Props {
  onClose: () => void;
  refetchData: () => Promise<void>;
  workflowID: string;
  requestAuthorisation?: string;
}

export const RenameWorkflowForm = ({
  workflowID,
  onClose,
  refetchData,
  requestAuthorisation,
}: Props): JSX.Element | null => {
  const {
    data: workflowData,
    isLoading,
    isLoadingError,
  } = useGetWorkflowDetails({
    pathParams: {
      workflowID: workflowID,
    },
  });

  if (isLoading === true) {
    return <Placeholder copy="Getting data..." />;
  }

  if (workflowData === undefined && isLoadingError === true) {
    return <Placeholder copy="Error loading workflow" />;
  }

  return (
    <FormWrapper
      workflow={workflowData}
      refetchData={refetchData}
      onClose={onClose}
      requestAuthorisation={requestAuthorisation}
    />
  );
};
